import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { DocumentsService } from '../../services/documents.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { FuseDeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'document-component',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss'],
  standalone: true,
  imports: [
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    TranslateModule
  ]
})
export class DocumentComponent {
  private readonly documentsService = inject(DocumentsService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastr = inject(ToastrService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  uploadedFilesInput = input<any[]>([]);
  eventData = input<any>();
  type = input<string>();
  id = input<any>();
  readOnly = input<boolean>(false);

  filesCount = output<{ count: number }>();
  filesToUpload = output<any[]>();
  filesAccess = output<any>();
  fileDeleted = output<any>();
  newFileDeleted = output<any>();

  files = signal<any[]>([]);
  uploadedFiles = signal<any[]>([]);
  publicInfo = signal<boolean[]>([]);

  totalCount = computed(() => this.files().length + this.uploadedFiles().length);
  uniqueId = input<string>('docInput_' + Math.random().toString(36).substr(2, 9));

  constructor() {
    effect(() => {
      const uploadedFilesInput = this.uploadedFilesInput();
      untracked(() => this.uploadedFiles.set(uploadedFilesInput || []));
    });

    effect(() => {
      const eventData = this.eventData();
      if (eventData?.id) {
        this.documentsService.getDocuments(eventData.id, 'event').subscribe((res: any) => {
          if (res?.length) {
            res.forEach(item => {
              item.url = item.name;
              item.type = item.name.substring(item.name.lastIndexOf('.') + 1);
              item.name = item.name.split('|_|')[1] || item.name;
            });
            untracked(() => this.uploadedFiles.set(res));
            this.emitFilesCount();
          }
        });
      }
    });
  }

  getFile(name: string) {
    return this.documentsService.getFileStream(name);
  }

  handleInputChangeDoc(event: any) {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) { return; }

    if (file.size > 2097152) {
      this.toastr.error(this.translate.instant('too_big_file'), this.translate.instant('failure'));
      return;
    }

    this.publicInfo.update(info => [...info, false]);
    // this.filesAccess.emit(this.publicInfo());

    const fileExtension = file.name.split('.').pop();
    const newFileEntry = {
      file: file,
      name: file.name,
      type: fileExtension,
      tempId: 'file_' + Math.random().toString(36).substr(2, 9),
    };

    const type = this.type();
    if (type === 'newEvent') {
      this.files.update(f => [...f, newFileEntry]);
      this.filesToUpload.emit(this.files());
      this.emitFilesCount();
    } else {
      this.spinner.show();
      const connectedId = type === 'newEmail' ? (this.id() || 0) : this.id();

      this.documentsService.uploadDocument({
        type: type,
        connectedId: connectedId,
        files: [{
          file: file,
          name: file.name,
          type: fileExtension
        }]
      }).subscribe((res: any) => {
        this.spinner.hide();
        if (res?.length) {
          this.toastr.success('Document added', 'Success!');
          const uploadedEntry = {
            id: res[0].id,
            url: res[0].name,
            file: file,
            name: file.name,
            type: fileExtension
          };
          this.files.update(f => [...f, uploadedEntry]);
          if (type === 'newEmail') {
            this.filesToUpload.emit(this.files());
          }
          this.emitFilesCount();
        }
      });
    }
    // Reset input
    event.target.value = '';
  }

  removeFile(file: any) {
    this.files.update(f => f.filter(item => item.tempId !== file.tempId));
    this.newFileDeleted.emit(file.tempId);
    this.emitFilesCount();
    if (file.id) {
      this.documentsService.deleteDocument(file.id).subscribe();
    }
  }

  removeFileFromUpload(file: any) {
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent, {
      panelClass: 'unsave-form-dialog',
      width: '300px',
      data: { title: 'Delete', message: 'deleteConfirm' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const fileId = file.id;
        this.uploadedFiles.update(f => f.filter(item => item.id !== fileId));
        this.fileDeleted.emit(file);
        this.emitFilesCount();
        this.documentsService.deleteDocument(file.id).subscribe();
      }
    });
  }

  showFiles(event: any, file: any) {
    this.filesAccess.emit({checked: event.checked, tempId: file.tempId});
  }

  changeState(event: any, id: any) {
    if (this.readOnly()) { return; }
    this.spinner.show();
    this.documentsService.updateAccess(id, event.checked).subscribe(() => {
      this.spinner.hide();
    });
  }

  private emitFilesCount() {
    this.filesCount.emit({ count: this.totalCount() });
  }
}
